import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ExternalLoginType, RemoteLoginResponse, User, UserSignUp } from 'src/app/models/user';
import { ApiService } from './api.service';
import { HttpClient } from '@angular/common/http';
import { BroadcastService } from './broadcast.service';
import { IdentifierType, ParameterType, RegisterParameter, SsoType } from '../models/client-configuration';
import { Util } from '../util';
import { StorageService } from './storage.service';
import dayjs from 'dayjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public ssoType: SsoType | null = null;

  constructor(
    private _http: HttpClient,
    private apiService: ApiService,
    private storageService: StorageService,
    private broadcastService: BroadcastService
  ) { }

  private static determineExternalType(params: any): ExternalLoginType {
    const keys = Object.keys(params);

    if (keys.includes('timestamp') && keys.includes('signature') && keys.includes('profile')) {
      return 'SAML2';
    } else if (keys.some((k) => k.toLowerCase() === 'x-username')) {
      return 'EZPROXY';
    } else if (keys.includes('code') || keys.includes('accessToken')) {
      return 'OAUTH2';
    } else if (keys.includes('profile') && keys.includes('hash')) {
      return 'OTK';
    } else if (keys.includes('profile')) {
      return 'CAS';
    } else if (keys.includes('token')) {
      return 'LEIASP';
    }

    return 'UNKNOWN';
  }

  public login(identifier: string, password: string, library?: string, userType?: number): Observable<User> {
    return this.apiService
      .login(identifier, password, library, userType)
      .pipe(map((user) => this.setUserStorageData(user)));
  }

  public remoteLogin(params: any): Observable<RemoteLoginResponse> {
    return this.apiService
      .remoteLogin(params)
      .pipe(tap((response) => response.user && this.setUserStorageData(response.user)));
  }

  public getExternalLoginUrl(loginUrl: string): Observable<string> {
    const url = new URL(loginUrl);
    const type = url.searchParams.get('type') as ExternalLoginType;
    const _callback = `${Util.getOrigin()}/login/callback`;

    if (!type) {
      let resolvedUrl = url.toString();

      if (this.ssoType === SsoType.EZPROXY) {
        const parsedUrl = new URL(resolvedUrl);
        parsedUrl.searchParams.set('qurl', window.location.href);

        resolvedUrl = parsedUrl.toString();
      }

      return of(resolvedUrl);
    }

    if (type === 'EZPROXY') {
      url.searchParams.append('redirectTo', window.location.pathname);
      let resolvedUrl = url.toString();

      if (this.ssoType === SsoType.EZPROXY) {
        const parsedUrl = new URL(resolvedUrl);
        parsedUrl.searchParams.set('qurl', window.location.href);

        resolvedUrl = parsedUrl.toString();
      }

      return of(resolvedUrl);
    }

    return this.apiService.getExternalLoginUrl(_callback, type)
      .pipe(
        map((res) => {
          if (this.ssoType === SsoType.EZPROXY) {
            const parsedUrl = new URL(res);
            parsedUrl.searchParams.set('qurl', window.location.href);

            res = parsedUrl.toString();
          }

          return res;
        })
      );
  }

  public externalLogin(params: any): Observable<User> {
    const type = AuthService.determineExternalType(params);

    return this.apiService.externalLogin(params, type).pipe(map((user) => this.setUserStorageData(user)));
  }

  public setUserStorageData(user: User): User {
    this.storageService.set('session', user.session);
    this.storageService.set('user', JSON.stringify(user));

    return user;
  }

  public logout(): Observable<any> {
    this.localLogout();
    return this.apiService.logout();
  }

  public localLogout(): void {
    this.storageService.remove('session');
    this.storageService.remove('user');
    this.storageService.remove('token');
    this.storageService.remove('profilePicture');
    this.storageService.remove('landing');
    localStorage.removeItem('redirectTo');
  }

  public register(values: any, multipleId?: number, code?: string): Observable<UserSignUp> {
    const userParameters: RegisterParameter[] = Object.keys(values).map((k) => {
      let value = values[k];

      if (value) {
        const key = Number(k);

        if (key === ParameterType.IDENTIFICADOR_EXTERNO) {
          value = value[k].field + '#_#' + value[k]['data-register']; // meh
        } else if (key === ParameterType.CONTRASENNA) {
          value = value[k].data;
        } else if (key === ParameterType.FECHA_NACIMIENTO) {
          value = dayjs(value).format('DD/MM/YYYY');
        }
      }

      return { type: ParameterType[k], value: value };
    });

    const loginType = values[ParameterType.IDENTIFICADOR_EXTERNO][0];

    if (loginType.field === IdentifierType.EMAIL.toString()) {
      userParameters.push({
        type: ParameterType.CORREO_ELECTRONICO as ParameterType,
        value: loginType['data-register'],
      });
    }

    return this.apiService.register(userParameters, multipleId, code);
  }

  public forgot(identifier: string): Observable<any> {
    return this.apiService.forgot(identifier);
  }

  public isAuthenticated(): boolean {
    const session: string = this.storageService.get('session');
    const user: User = JSON.parse(this.storageService.get('user'));

    return user && user.session === session;
  }

  public getLoggedUser(): User {
    return JSON.parse(this.storageService.get('user'));
  }

  public updateLoggedUser(user: User): User {
    delete user.currentPassword;
    delete user.newPassword;

    let loggedUser = JSON.parse(this.storageService.get('user'));
    const session = loggedUser.session;

    loggedUser = Object.assign(loggedUser, user);
    loggedUser.session = user.session || session;

    this.storageService.set('user', JSON.stringify(loggedUser));

    this.loadProfilePicture(loggedUser).subscribe();
    return user;
  }

  public loadProfilePicture(user: User): Observable<void> {
    if (!user.profilePictureUrl) {
      return of();
    }

    return this._http.get<Blob>(user.profilePictureUrl, { responseType: 'blob' as 'json' }).pipe(
      map((data) => {
        const ext = user.profilePictureUrl.split('.').pop().split('?').shift();
        const reader = new FileReader();

        reader.addEventListener(
          'load',
          () => {
            const dataUrl = reader.result as string;
            const imageData = dataUrl.replace('data:application/octet-stream;', `data:image/${ext};`);

            this.storageService.set('profilePicture', imageData);
            this.broadcastService.broadcastProfilePicture(true);
          },
          false
        );

        reader.readAsDataURL(data);
      })
    );
  }

  getUserInfo() {
    const apiUrl = `${this.apiService.API_URL}/patrons/local`;
    return this._http.get<User>(apiUrl).pipe(
      tap((userInfo) => this.updateLoggedUser(userInfo)));
  }
}
